import { fromBinary, toJsonString } from '@bufbuild/protobuf';
import { useLocalObservable } from 'mobx-react-lite';
import { ChangeEvent, ClipboardEvent } from 'react';

import {
  CallWsRequestSchema,
  CallWsResponseSchema,
} from '@gem/call/WsApi/wsCallsApi/types/call_websocket_pb';
import { isLogEnabled, setIsLogEnabled } from '@gem/modules/logger';
import { ICommonPopupsProps } from '@gem/popups';

import { Features } from 'APP/packages/features/Features';
import { WsRequestSchema, WsResponseSchema } from 'APP/wsApi/wsAppApi/types/websocket_pb';

function hasUnknownField(obj: any): boolean {
  if (obj && typeof obj === 'object') {
    if ('$unknown' in obj) {
      return true;
    }
    for (const key in obj) {
      if (
        Object.prototype.hasOwnProperty.call(obj, key) &&
        typeof obj[key] === 'object' &&
        hasUnknownField(obj[key])
      ) {
        return true;
      }
    }
  }
  return false;
}

function hexStringToArrayBuffer(hexString: string): ArrayBuffer {
  const length = hexString.length / 2;
  const arrayBuffer = new ArrayBuffer(length);
  const uint8Array = new Uint8Array(arrayBuffer);

  for (let i = 0; i < length; i++) {
    uint8Array[i] = parseInt(hexString.slice(i * 2, i * 2 + 2), 16);
  }

  return arrayBuffer;
}

function parseWsApp(binaryData: ArrayBuffer): string {
  try {
    const wsResponse = fromBinary(WsResponseSchema, new Uint8Array(binaryData));

    if (hasUnknownField(wsResponse)) {
      throw Error('unknown fields');
    }
    const parsedValue = toJsonString(WsResponseSchema, wsResponse, {
      alwaysEmitImplicit: true,
      useProtoFieldName: true,
      prettySpaces: 2,
    });

    return parsedValue;
  } catch (e) {
    //
  }

  try {
    const wsRequest = fromBinary(WsRequestSchema, new Uint8Array(binaryData));
    if (hasUnknownField(wsRequest)) {
      throw Error('unknown fields');
    }
    const parsedValue = toJsonString(WsRequestSchema, wsRequest, {
      useProtoFieldName: true,
      prettySpaces: 2,
    });

    return parsedValue;
  } catch (e) {
    //
  }
  return 'Could not parse message';
}

function parseWsCalls(binaryData: ArrayBuffer): string {
  try {
    const wsCallsResponse = fromBinary(CallWsResponseSchema, new Uint8Array(binaryData));

    if (hasUnknownField(wsCallsResponse)) {
      throw Error('unknown fields');
    }
    return toJsonString(CallWsResponseSchema, wsCallsResponse, {
      alwaysEmitImplicit: true,
      useProtoFieldName: true,
      prettySpaces: 2,
    });
  } catch (e) {
    //
  }

  try {
    const wsCallRequest = fromBinary(CallWsRequestSchema, new Uint8Array(binaryData));
    if (hasUnknownField(wsCallRequest)) {
      throw Error('unknown fields');
    }
    return toJsonString(CallWsRequestSchema, wsCallRequest, {
      useProtoFieldName: true,
      prettySpaces: 2,
    });
  } catch (e) {
    //
  }
  return 'Could not parse message';
}

interface IDevToolsPopupPresenter {
  parsedValue: string;
  socketIndex: number;
  isLogEnabled: boolean;
  isShowLogSwitch: boolean;
  isReset: boolean;

  onSocketClick(index: number): void;
  onLogEnabledChange(event: ChangeEvent<HTMLInputElement>): void;
  onPaste(event: ClipboardEvent<HTMLTextAreaElement>): Promise<void>;
  onReset(): void;
  onClosePopup(): void;
}

export interface IDevToolsPopupParams {
  devToolKey: string;
}

export const useDevToolsPopupPresenter = ({
  popupInstance,
  params,
}: ICommonPopupsProps<IDevToolsPopupParams>): IDevToolsPopupPresenter => {
  const presenter = useLocalObservable<IDevToolsPopupPresenter>(() => ({
    parsedValue: '',
    socketIndex: 0,
    isLogEnabled: isLogEnabled(),
    isShowLogSwitch: !Features.isLogEnabled(),
    isReset: false,

    onClosePopup(): void {
      popupInstance.close();
    },

    onReset(): void {
      this.isReset = true;
      setIsLogEnabled(false);
      if (params?.devToolKey) {
        localStorage.setItem(params.devToolKey, 'false');
      }
    },

    onLogEnabledChange(event: ChangeEvent<HTMLInputElement>): void {
      presenter.isLogEnabled = event.target.checked;
      setIsLogEnabled(presenter.isLogEnabled);
    },

    onSocketClick(index: number): void {
      presenter.parsedValue = '';
      presenter.socketIndex = index;
    },

    async onPaste(event: ClipboardEvent<HTMLTextAreaElement>): Promise<void> {
      const hexString = event.clipboardData.getData('text/plain');

      const binaryData = hexStringToArrayBuffer(hexString);

      presenter.parsedValue =
        //TODO: implement for calls later
        presenter.socketIndex === 0 ? parseWsApp(binaryData) : parseWsCalls(binaryData);
    },
  }));

  return presenter;
};
